import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, CheckCircle, Users, Target, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Accuracy & Precision",
      description: "Every number matters. We take meticulous care to ensure your financial records are accurate down to the last penny."
    },
    {
      icon: Heart,
      title: "Client-Focused Approach",
      description: "Your success is our priority. We take time to understand your business and provide personalised support."
    },
    {
      icon: Target,
      title: "Complete Confidentiality",
      description: "Your financial information is sensitive. We maintain strict confidentiality and robust data protection practices."
    },
    {
      icon: Users,
      title: "Clear Communication",
      description: "No confusing jargon. We explain everything in plain English so you always understand your finances."
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-teal/5 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-navy mb-6">
                About Wonderman Bookkeeping
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-6">
                We're not just number crunchers—we're your financial partners. Our mission is to help small businesses 
                and freelancers take control of their finances with confidence.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                With professional qualifications, years of experience, and a genuine passion for helping businesses succeed, 
                we provide bookkeeping services that are accurate, compliant, and refreshingly stress-free.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-teal/10 rounded-3xl transform rotate-6"></div>
                <img 
                  src={businessInfo.logo} 
                  alt="Wonderman Bookkeeping" 
                  className="relative w-80 h-auto rounded-3xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4 justify-center">
              <div className="w-14 h-14 rounded-full bg-teal/20 flex items-center justify-center">
                <Award className="text-amber" size={28} />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">QuickBooks Pro Advisor</p>
                <p className="text-slate-300">Certified specialist</p>
              </div>
            </div>
            <div className="flex items-center gap-4 justify-center">
              <div className="w-14 h-14 rounded-full bg-teal/20 flex items-center justify-center">
                <CheckCircle className="text-amber" size={28} />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">MTD Ready</p>
                <p className="text-slate-300">Making Tax Digital compliant</p>
              </div>
            </div>
            <div className="flex items-center gap-4 justify-center">
              <div className="w-14 h-14 rounded-full bg-teal/20 flex items-center justify-center">
                <Shield className="text-amber" size={28} />
              </div>
              <div>
                <p className="font-semibold text-white text-lg">AML Compliant</p>
                <p className="text-slate-300">Registered with HMRC & ICB</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
              Our Values
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything we do is guided by our commitment to accuracy, integrity, and exceptional client service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border border-slate-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <value.icon className="text-teal" size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-navy mb-3">{value.title}</h3>
                      <p className="text-slate-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-6">
                Why Work With Us?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Experienced & Qualified</h3>
                    <p className="text-slate-600">Professional qualifications backed by real-world experience with businesses of all sizes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Fixed, Transparent Pricing</h3>
                    <p className="text-slate-600">No hourly rates or surprise bills. You'll always know exactly what you're paying.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Cloud-Based & Secure</h3>
                    <p className="text-slate-600">Access your financial data anytime, anywhere with bank-level security.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Personal, Responsive Service</h3>
                    <p className="text-slate-600">You'll work with a real person who knows your business—not a call centre.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 lg:p-12 rounded-3xl shadow-xl">
              <h3 className="text-2xl font-bold text-navy mb-6">Ready to Chat?</h3>
              <p className="text-slate-600 mb-8">
                We'd love to hear about your business and discuss how we can help. 
                No pressure, no obligation—just friendly, honest advice.
              </p>
              <div className="space-y-4">
                <a href={`tel:${businessInfo.phone}`} className="block">
                  <Button size="lg" className="w-full bg-amber hover:bg-amber-dark text-navy font-semibold h-14 text-lg">
                    Call: {businessInfo.phone}
                  </Button>
                </a>
                <a href={`mailto:${businessInfo.email}?subject=Bookkeeping%20Enquiry&body=Hello%20Wonderman%20Bookkeeping%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20bookkeeping%20services.%0A%0AName%3A%20%0ABusiness%20Name%3A%20%0APhone%3A%20%0A%0APlease%20get%20in%20touch%20at%20your%20earliest%20convenience.%0A%0AThank%20you.`} className="block">
                  <Button size="lg" variant="outline" className="w-full border-2 border-teal text-teal hover:bg-teal hover:text-white h-14 text-lg">
                    Send a Message
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
